import {gql} from '@apollo/client';

import {ProteinCreationTableRequest} from 'antibodies/nested-data-creation/full-screen/ProteinCreationTypes';
import {
  AntibodyComponentAndRelatedSchemasQuery,
  AntibodyComponentAndRelatedSchemasQueryVariables,
} from 'antibodies/nested-data-creation/spreadsheet-import/__generated/AntibodyComponentAndRelatedSchemasQuery';
import {
  ProteinSchemaLinkedSchemasQuery,
  ProteinSchemaLinkedSchemasQueryVariables,
} from 'antibodies/nested-data-creation/spreadsheet-import/__generated/ProteinSchemaLinkedSchemasQuery';
import {
  AntibodyComponentAndRelatedSchemas,
  NdcColumnInferenceRequest,
  NdcColumnInferenceResponse,
  NdcColumnInferenceResult,
  NdcSpreadsheetImportRequest,
  NdcSpreadsheetImportResponse,
  NdcSpreadsheetImportSubmissionValidationErrors,
  AntibodyComponentSchemaInfo,
  ProteinCreationSpreadsheetParseRequest,
  ProteinCreationSpreadsheetParseResponse,
} from 'antibodies/nested-data-creation/spreadsheet-import/constants/AntibodyNestedDataCreationSpreadsheetImportConstants';
import {
  AntibodyComponentSchemaRole,
  AntibodyDomainType,
  AntibodyRelatedSchemaRole,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as Net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

export function parseProteinCreationSpreadsheet(
  input: ProteinCreationSpreadsheetParseRequest
): Promise<ProteinCreationSpreadsheetParseResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/proteins-and-components:parse-protein-creation-spreadsheet',
    data: input,
  });
}

export async function inferColumnsForNestedCreateAndRegisterSpreadsheet(
  input: NdcColumnInferenceRequest
): Promise<NdcColumnInferenceResponse> {
  const {
    inferredColumns,
    rowCount,
  }: {
    inferredColumns: Array<NdcColumnInferenceResult>;
    rowCount: number;
  } = await TaskRunner.postJSONPromise({
    url: '/1/api/proteins-and-components:infer-columns-from-nested-create-and-register-spreadsheet',
    data: input,
  });

  return {
    columnTypes: inferredColumns,
    // Backend returns data rows only (header row excluded).
    rowCount,
  };
}

export function nestedCreateAndRegisterFromSpreadsheetImport(
  input: NdcSpreadsheetImportRequest
): Promise<NdcSpreadsheetImportResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/proteins-and-components:nested-create-and-register-from-spreadsheet',
    data: input,
  });
}

export async function validateProteinCreationInput(
  request: ProteinCreationTableRequest
): Promise<NdcSpreadsheetImportSubmissionValidationErrors> {
  const {validationErrors, batchErrors}: NdcSpreadsheetImportSubmissionValidationErrors =
    await Net.postJSONPromise({
      url: '/1/api/proteins-and-components:validate-protein-creation-input',
      data: request,
    });
  return {validationErrors, batchErrors};
}

const ANTIBODY_COMPONENT_AND_RELATED_SCHEMAS_QUERY = gql`
  query AntibodyComponentAndRelatedSchemasQuery($registryId: StrictString!, $proteinSchemaId: StrictString!)
  @skipAudit
  @engComponent(name: MOLBIO) {
    registry(id: $registryId) {
      ... on RegistryNode {
        id
        validAntibodySchema(id: $proteinSchemaId) {
          id
          componentSchemas {
            role
            id
            name
            distinctComponentKey {
              ... on DistinctDomainKeyNode {
                label
              }
              ... on DistinctChainKeyNode {
                label
              }
            }
          }
        }
        antibodyRelatedSchemas {
          id
          name
          role
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export type AntibodyComponentSchema = NonNullable<
  ExcludeInaccessible<AntibodyComponentAndRelatedSchemasQuery['registry']>['validAntibodySchema']
>['componentSchemas'][number];

/** Extracts component schemas by role and deduplicates by the schema ID. Component schemas can contain
 * duplicates because the backend returns one entry per slot in the antibody (e.g., both VH and VL domains may
 * reference the same schema). Exported only for testing. */
export function _getUniqueComponentSchemasWithRole(
  schemas: Array<AntibodyComponentSchema>,
  role: AntibodyComponentSchemaRole
): Array<AntibodyComponentSchemaInfo> {
  const schemasWithRole = schemas.filter((schema) => schema.role === role);
  return [
    ...new Map(schemasWithRole.map((schema) => [schema.id, {id: schema.id, name: schema.name}])).values(),
  ];
}

export interface QueryAntibodyComponentAndRelatedSchemasInput {
  registryId: string;
  proteinSchemaId: string;
}

export async function queryAntibodyComponentAndRelatedSchemas({
  registryId,
  proteinSchemaId,
}: QueryAntibodyComponentAndRelatedSchemasInput): Promise<AntibodyComponentAndRelatedSchemas> {
  const result = await doQuerySafe<
    AntibodyComponentAndRelatedSchemasQuery,
    AntibodyComponentAndRelatedSchemasQueryVariables
  >({query: ANTIBODY_COMPONENT_AND_RELATED_SCHEMAS_QUERY}, {variables: {registryId, proteinSchemaId}});

  const registry = result.registry;
  if (isInaccessible(registry)) {
    throw new Error(`Registry ${registryId} is inaccessible`);
  }

  const antibodySchema = registry.validAntibodySchema;
  if (antibodySchema == null) {
    throw new Error(`Protein schema ${proteinSchemaId} not found in registry ${registryId}`);
  }

  const aaDomainSchemas = _getUniqueComponentSchemasWithRole(
    antibodySchema.componentSchemas,
    AntibodyComponentSchemaRole.AA_DOMAIN
  );
  const aaChainSchemas = _getUniqueComponentSchemasWithRole(
    antibodySchema.componentSchemas,
    AntibodyComponentSchemaRole.AA_CHAIN
  );

  const antibodyRelatedSchemas = registry.antibodyRelatedSchemas;
  const dnaDomainSchemaIds = new Set(
    antibodyRelatedSchemas.filter(({role}) => role === AntibodyRelatedSchemaRole.DNA_DOMAIN).map(({id}) => id)
  );
  const dnaChainSchemaIds = new Set(
    antibodyRelatedSchemas.filter(({role}) => role === AntibodyRelatedSchemaRole.DNA_CHAIN).map(({id}) => id)
  );
  const dnaPairSchemaIds = new Set(
    antibodyRelatedSchemas.filter(({role}) => role === AntibodyRelatedSchemaRole.DNA_PAIR).map(({id}) => id)
  );
  const aaPairSchemaIds = new Set(
    antibodyRelatedSchemas.filter(({role}) => role === AntibodyRelatedSchemaRole.AA_PAIR).map(({id}) => id)
  );

  return {
    aaDomainSchemas,
    aaChainSchemas,
    dnaDomainSchemaIds,
    dnaChainSchemaIds,
    dnaPairSchemaIds,
    aaPairSchemaIds,
  };
}

// ---------------------------------------------------------------------------
// SSI linked-schema query: maps each domain/chain to its configured AA schema
// ---------------------------------------------------------------------------

interface DomainLinkedSchema {
  domainType: AntibodyDomainType;
  specificity: string | null;
  schemaOrInterfaceId: string | null;
}

interface ChainLinkedSchema {
  label: string;
  schemaOrInterfaceId: string | null;
}

const PROTEIN_SCHEMA_LINKED_SCHEMAS_QUERY = gql`
  query ProteinSchemaLinkedSchemasQuery($schemaId: StrictString!) @engComponent(name: MOLBIO) @skipAudit {
    implementedAntibodySchemaInterface(schemaId: $schemaId) {
      ...ImplementedAntibodySchemaInterfaceGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ImplementedAntibodySchemaInterface').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface ProteinSchemaLinkedSchemas {
  domainLinkedSchemas: Array<DomainLinkedSchema>;
  chainLinkedSchemas: Array<ChainLinkedSchema>;
}

export async function queryProteinSchemaLinkedSchemas(
  proteinSchemaId: string
): Promise<ProteinSchemaLinkedSchemas | null> {
  const result = await doQuerySafe<ProteinSchemaLinkedSchemasQuery, ProteinSchemaLinkedSchemasQueryVariables>(
    {query: PROTEIN_SCHEMA_LINKED_SCHEMAS_QUERY},
    {variables: {schemaId: proteinSchemaId}}
  );

  const schemaInterface = result.implementedAntibodySchemaInterface;
  if (schemaInterface == null || isInaccessible(schemaInterface)) {
    return null;
  }
  return schemaInterface;
}
